// EventConfig.jsx
// Unified per-event setup. Saved profiles ("Tower Grove Market", "College
// Activation") bundle everything the operator sets between venues: shift-open
// defaults (event, venue, artists, context, flash sets, /join, messaging
// suppressions) + overlay retuning (theme, titles, QR, break/outro copy).
//
// Architecture: profiles are STAMPS, not live references. "Apply" copies
// values into the places runtime consumers already read:
//   - overlay keys → config/stream (merge)  → all 8 overlay scenes retune
//     immediately via their existing onSnapshot subscriptions
//   - shiftDefaults → config/eventSetup     → ShiftManager pre-fills its open
//     form from this staging doc and stamps the values onto the new shift doc
// Nothing at runtime reads eventProfiles/* directly, so old shifts and a
// skipped Event Config behave exactly as before.
//
// The config/eventSetup handoff is ONE-SHOT: ShiftManager marks it consumed
// (consumedByShiftId) when the staged shift opens, and ignores stagings older
// than 18h, so last week's setup can never leak into an unrelated shift.

import { useEffect, useRef, useState } from "react";
import { db } from "../../firebase";
import { resolveShift } from "../../utils/shiftScope";
import {
  collection, doc, addDoc, updateDoc, setDoc, deleteDoc,
  onSnapshot, query, where, orderBy, serverTimestamp,
} from "firebase/firestore";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import ShiftMessagingCard from "../../components/ShiftMessagingCard";
import { OVERLAY_THEMES } from "../../utils/overlayTheme";
import { COMBOS, COMBO_ORDER, fmtCents } from "../../utils/combos";
import { EXTRA_PRESETS } from "../../utils/eventPricing";
import { STAFF_THEME } from "../../theme/staff";

const BRAND = STAFF_THEME;

// Overlay text fields staged onto config/stream by Apply. Blank = leave the
// current Stream Setup value untouched (sparse overrides — a profile only
// owns the copy it explicitly sets).
const OVERLAY_TEXT_FIELDS = [
  { key: "streamTitle",    label: "Starting Soon title" },
  { key: "streamSubtitle", label: "Starting Soon subtitle" },
  { key: "customQrUrl",      label: "QR destination URL" },
  { key: "customQrLabel",    label: "QR headline" },
  { key: "customQrSublabel", label: "QR subtext" },
  { key: "breakMessage",    label: "Break message" },
  { key: "breakReturnTime", label: "Break return time" },
  { key: "outroMessage",    label: "Outro sign-off" },
];

// Seed for enabling Road Mode pricing — Chicago-ready defaults; every number
// is editable before Apply. Cents everywhere (display converts).
function roadPricingSeed(eventName) {
  return {
    flashCents: 1500,
    customCents: 4000,
    combos: { artist: 5500, collector: 7000 },
    extras: EXTRA_PRESETS.map((e) => ({ ...e })),
    taxPct: 10.75,
    taxLabel: "IL sales tax",
    eventTag: (eventName || "").trim() || "Pop-Up",
    squareLocationName: "",
    menuNote: "Preservation kits $15 · framed $20 — ask at the booth",
    hideStandby: true,
  };
}

// Dollar input over a cents value. Blank/invalid → null (field unset).
function PriceInput({ cents, onCents, placeholder, width = "88px" }) {
  return (
    <input
      inputMode="decimal"
      value={cents == null ? "" : (cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2))}
      onChange={(e) => {
        const v = e.target.value.replace(/[^0-9.]/g, "");
        if (v === "") { onCents(null); return; }
        const n = Number(v);
        onCents(Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null);
      }}
      placeholder={placeholder || "$"}
      style={{ ...inputStyle, width, flex: "0 0 auto", textAlign: "right" }}
    />
  );
}

function blankDraft() {
  return {
    name: "",
    icon: "🎪",
    shiftDefaults: {
      eventName: "",
      venueAddress: "",
      venueIsPublic: true,
      artistRoster: ["Dan"],
      contextMode: "stream",
      // Ordering voice for /join — "off" | "free" (party) | "paid" (market).
      // Seeds shift.qrFlows.mode at open so a party profile opens the shift
      // in party voice (scans go straight to the main queue, no standby).
      joinMode: "off",
      livestreamJoinEnabled: false,
      activeFlashProfileIds: [],
      includeBaseFlashSet: true,
      // Combo offers for this event (spotlight card, /join interstitial,
      // portal upgrade). Free/party voice suppresses combos regardless, so
      // party profiles can leave this as-is.
      activeComboKeys: [...COMBO_ORDER],
      disabledMarqueeTexts: [],
      disabledShowcaseIds: [],
      // Road Mode: per-event pricing block (null = home prices, tipping off,
      // no event tax — exactly today's behavior). Stamped onto the shift at
      // open and DYING with it: closing the shift is the whole revert.
      pricing: null,
    },
    overlay: {
      overlayTheme: "classic",
      // Stamped on every Apply (never blank-skipped), so a profile without
      // gala mode actively turns it off — no leak-forward to the next venue.
      boothGalaMode: false,
      streamTitle: "", streamSubtitle: "",
      customQrUrl: "", customQrLabel: "", customQrSublabel: "",
      breakMessage: "", breakReturnTime: "",
      outroMessage: "",
    },
  };
}

// Merge a stored profile doc over the blank scaffold so missing keys (older
// profiles after future field additions) never produce undefined inputs.
function normalizeProfile(data) {
  const base = blankDraft();
  const shiftDefaults = { ...base.shiftDefaults, ...(data?.shiftDefaults || {}) };
  // Profiles saved before joinMode existed carry only the boolean gate —
  // derive market/off from it so an old "join on" profile stays a market
  // profile instead of silently normalizing to off.
  if (!["off", "free", "paid"].includes(shiftDefaults.joinMode)) {
    shiftDefaults.joinMode = shiftDefaults.livestreamJoinEnabled ? "paid" : "off";
  }
  return {
    ...base,
    name: data?.name || "",
    icon: data?.icon || base.icon,
    shiftDefaults,
    overlay: { ...base.overlay, ...(data?.overlay || {}) },
  };
}

// Seed a new profile from this device's last-used shift settings (the same
// localStorage stickies ShiftManager keeps) — first-run population beats a
// blank form, and a wrong guess is one tap to fix.
function draftFromDeviceStickies() {
  const d = blankDraft();
  try {
    d.shiftDefaults.eventName    = localStorage.getItem("gt_event_name") || "";
    d.shiftDefaults.venueAddress = localStorage.getItem("gt_venue_address") || "";
    d.shiftDefaults.venueIsPublic = localStorage.getItem("gt_venue_is_public") !== "false";
    d.shiftDefaults.artistRoster = JSON.parse(localStorage.getItem("gt_artists")) || ["Dan"];
    const jm = localStorage.getItem("gt_default_join_mode");
    d.shiftDefaults.joinMode = ["off", "free", "paid"].includes(jm)
      ? jm
      : (localStorage.getItem("gt_default_stream_join") === "true" ? "paid" : "off");
    d.shiftDefaults.livestreamJoinEnabled = d.shiftDefaults.joinMode !== "off";
    const ctx = localStorage.getItem("gt_default_context_mode");
    if (ctx === "stream" || ctx === "booth" || ctx === "both") d.shiftDefaults.contextMode = ctx;
    d.shiftDefaults.activeFlashProfileIds = JSON.parse(localStorage.getItem("gt_selected_flash_profiles")) || [];
    d.shiftDefaults.includeBaseFlashSet = localStorage.getItem("gt_include_base_flash_set") !== "false";
  } catch { /* partial seed is fine */ }
  return d;
}

function timeAgo(ms) {
  if (!ms) return "";
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

// ── Shared UI scraps ─────────────────────────────────────────────────────────
function Section({ title, hint, children }) {
  return (
    <div style={{
      width: "100%", boxSizing: "border-box",
      background: BRAND.card, border: `1px solid ${BRAND.border}`,
      borderRadius: "12px", padding: "0.85rem 1rem",
      display: "flex", flexDirection: "column", gap: "0.6rem",
    }}>
      <div style={{ color: "white", fontSize: "0.95rem", fontWeight: 700 }}>{title}</div>
      {hint && <div style={{ color: BRAND.dim, fontSize: "0.75rem", lineHeight: 1.4, marginTop: "-0.3rem" }}>{hint}</div>}
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: BRAND.panel, border: `1px solid ${BRAND.border}`,
  borderRadius: "8px", color: "white", fontSize: "0.88rem", padding: "0.55rem 0.75rem",
};

function ToggleRow({ checked, onChange, label, sublabel, accent = BRAND.sky }) {
  return (
    <label style={{
      display: "flex", alignItems: "flex-start", gap: "0.6rem",
      background: BRAND.panel, border: `1px solid ${checked ? accent : BRAND.border}`,
      borderRadius: "8px", padding: "0.55rem 0.75rem",
      cursor: "pointer", userSelect: "none",
    }}>
      <input
        type="checkbox" checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: "2px", accentColor: accent, cursor: "pointer" }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ color: checked ? accent : "white", fontSize: "0.85rem", fontWeight: 600 }}>{label}</div>
        {sublabel && <div style={{ color: BRAND.dim, fontSize: "0.72rem", marginTop: "2px", lineHeight: 1.35 }}>{sublabel}</div>}
      </div>
    </label>
  );
}

export default function EventConfig({ navigate }) {
  // ── Saved profiles ─────────────────────────────────────────────────────────
  const [profiles, setProfiles] = useState(null);
  useEffect(() => {
    const q = query(collection(db, "eventProfiles"), orderBy("name"));
    return onSnapshot(q,
      (snap) => setProfiles(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setProfiles([]));
  }, []);

  // ── Working draft ──────────────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraftRaw] = useState(() => draftFromDeviceStickies());
  // Snapshot of the draft as loaded — dirty when the JSON diverges.
  const loadedRef = useRef(JSON.stringify(draftFromDeviceStickies()));
  const dirty = JSON.stringify(draft) !== loadedRef.current;

  function setDraft(updater) {
    setDraftRaw(prev => (typeof updater === "function" ? updater(prev) : updater));
  }
  function patchShift(patch) {
    setDraft(prev => ({ ...prev, shiftDefaults: { ...prev.shiftDefaults, ...patch } }));
  }
  function patchOverlay(patch) {
    setDraft(prev => ({ ...prev, overlay: { ...prev.overlay, ...patch } }));
  }

  function loadProfile(p) {
    if (dirty && !window.confirm("Discard unsaved changes and load this profile?")) return;
    const next = normalizeProfile(p);
    setSelectedId(p.id);
    setDraftRaw(next);
    loadedRef.current = JSON.stringify(next);
  }

  function startNewProfile() {
    if (dirty && !window.confirm("Discard unsaved changes and start a new profile?")) return;
    const next = draftFromDeviceStickies();
    setSelectedId(null);
    setDraftRaw(next);
    loadedRef.current = JSON.stringify(next);
  }

  // ── Source data for pickers ────────────────────────────────────────────────
  const [streamConfig, setStreamConfig] = useState(null);
  const [showcaseConfig, setShowcaseConfig] = useState(null);
  const [flashProfiles, setFlashProfiles] = useState(null);
  const [activeShift, setActiveShift] = useState(null);
  const [stagedSetup, setStagedSetup] = useState(null);
  useEffect(() => onSnapshot(doc(db, "config", "stream"),
    (s) => setStreamConfig(s.exists() ? s.data() : {}), () => setStreamConfig({})), []);
  useEffect(() => onSnapshot(doc(db, "config", "boothShowcase"),
    (s) => setShowcaseConfig(s.exists() ? s.data() : { items: [] }), () => setShowcaseConfig({ items: [] })), []);
  useEffect(() => onSnapshot(query(collection(db, "flashDesignProfiles"), orderBy("name")),
    (s) => setFlashProfiles(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setFlashProfiles([])), []);
  // Scoped (2026-09-25) — Apply writes onto the active shift, so it has to be
  // the one THIS device is working, not whichever came back first.
  useEffect(() => onSnapshot(query(collection(db, "shifts"), where("isActive", "==", true)),
    (s) => setActiveShift(resolveShift(s.docs.map(d => ({ id: d.id, ...d.data() }))).shift),
    () => setActiveShift(null)), []);
  useEffect(() => onSnapshot(doc(db, "config", "eventSetup"),
    (s) => setStagedSetup(s.exists() ? s.data() : null), () => setStagedSetup(null)), []);

  const [newArtist, setNewArtist] = useState(false);
  const [artistInput, setArtistInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [toast, setToast] = useState(null);
  function flash(msg) { setToast(msg); setTimeout(() => setToast(null), 2600); }

  // ── Save / Save-as / Delete ────────────────────────────────────────────────
  function profilePayload() {
    return {
      name: (draft.name || draft.shiftDefaults.eventName || "Untitled profile").trim(),
      icon: draft.icon || "🎪",
      shiftDefaults: draft.shiftDefaults,
      overlay: draft.overlay,
      updatedAt: serverTimestamp(),
    };
  }

  async function saveProfile() {
    setSaving(true);
    try {
      if (selectedId) {
        await updateDoc(doc(db, "eventProfiles", selectedId), profilePayload());
      } else {
        const name = window.prompt("Name this profile:", draft.name || draft.shiftDefaults.eventName || "");
        if (!name || !name.trim()) { setSaving(false); return; }
        const ref = await addDoc(collection(db, "eventProfiles"), {
          ...profilePayload(), name: name.trim(), createdAt: serverTimestamp(),
        });
        setSelectedId(ref.id);
        const next = { ...draft, name: name.trim() };
        setDraftRaw(next);
        loadedRef.current = JSON.stringify(next);
        flash("💾 Profile saved");
        setSaving(false);
        return;
      }
      // Re-snapshot against what's on screen so the dirty dot clears.
      loadedRef.current = JSON.stringify(draft);
      flash("💾 Profile saved");
    } catch (e) {
      alert("Save failed: " + (e?.message || e));
    }
    setSaving(false);
  }

  async function saveAsNew() {
    const name = window.prompt("Name for the new profile:", draft.name ? `${draft.name} (copy)` : draft.shiftDefaults.eventName || "");
    if (!name || !name.trim()) return;
    setSaving(true);
    try {
      const ref = await addDoc(collection(db, "eventProfiles"), {
        ...profilePayload(), name: name.trim(), createdAt: serverTimestamp(),
      });
      setSelectedId(ref.id);
      setDraft(prev => ({ ...prev, name: name.trim() }));
      loadedRef.current = JSON.stringify({ ...draft, name: name.trim() });
      flash("💾 Saved as new profile");
    } catch (e) {
      alert("Save failed: " + (e?.message || e));
    }
    setSaving(false);
  }

  async function deleteProfile() {
    if (!selectedId) return;
    if (!window.confirm(`Delete profile "${draft.name || "Untitled"}"? This can't be undone.`)) return;
    try {
      await deleteDoc(doc(db, "eventProfiles", selectedId));
      startNewProfileForce();
      flash("🗑 Profile deleted");
    } catch (e) {
      alert("Delete failed: " + (e?.message || e));
    }
  }
  function startNewProfileForce() {
    const next = draftFromDeviceStickies();
    setSelectedId(null);
    setDraftRaw(next);
    loadedRef.current = JSON.stringify(next);
  }

  // ── Apply ──────────────────────────────────────────────────────────────────
  // 1) overlay keys → config/stream (theme always; copy fields only when set)
  // 2) shiftDefaults → config/eventSetup (one-shot staging for the next open)
  async function applyDraft() {
    setApplying(true);
    try {
      const o = draft.overlay || {};
      const streamPatch = {
        overlayTheme: o.overlayTheme || "classic",
        boothGalaMode: !!o.boothGalaMode,
        // Preview stamp: lets overlays render the staged flash sets while no
        // shift is open (useFlashDesigns previewWhenIdle). A live shift's
        // activeFlashProfileIds always wins over these.
        previewFlashProfileIds: draft.shiftDefaults.activeFlashProfileIds || [],
        previewIncludeBaseFlashSet: draft.shiftDefaults.includeBaseFlashSet !== false,
        // Combo preview stamp — Lab test shifts seed activeComboKeys from it
        // (mirrors the flash preview pattern).
        previewComboKeys: draft.shiftDefaults.activeComboKeys || [],
      };
      for (const f of OVERLAY_TEXT_FIELDS) {
        const v = (o[f.key] || "").trim();
        if (v) streamPatch[f.key] = v;
      }
      await setDoc(doc(db, "config", "stream"), streamPatch, { merge: true });

      await setDoc(doc(db, "config", "eventSetup"), {
        profileId: selectedId || null,
        profileName: draft.name || draft.shiftDefaults.eventName || null,
        shiftDefaults: draft.shiftDefaults,
        appliedAt: serverTimestamp(),
        consumedByShiftId: null,
      });

      if (selectedId) {
        updateDoc(doc(db, "eventProfiles", selectedId), { lastUsedAt: serverTimestamp() }).catch(() => {});
      }
      flash(activeShift ? "⚡ Overlays updated · staged for next shift" : "⚡ Applied — open the queue when ready");
    } catch (e) {
      alert("Apply failed: " + (e?.message || e));
    }
    setApplying(false);
  }

  // ── Derived bits ───────────────────────────────────────────────────────────
  const sd = draft.shiftDefaults;
  const stagedFresh = stagedSetup && !stagedSetup.consumedByShiftId
    && (stagedSetup.appliedAt?.toMillis?.() ?? 0) > Date.now() - 18 * 3600 * 1000;

  return (
    <div style={{ maxWidth: "560px", margin: "0 auto", padding: "1rem 1rem 6rem", display: "flex", flexDirection: "column", gap: "0.9rem" }}>

      {/* ── Status strip ── */}
      {activeShift && (
        <div style={{
          background: "rgba(252,179,31,0.1)", border: `1px solid ${BRAND.gold}`,
          borderRadius: "10px", padding: "0.6rem 0.85rem", color: BRAND.gold, fontSize: "0.8rem", lineHeight: 1.45,
        }}>
          🔴 A shift is live ({activeShift.eventName || "unnamed"}). Apply retunes the overlays <b>now</b>, and
          stages the shift settings for the <b>next</b> queue open. Live-shift toggles (context, messaging,
          /join) stay on the Queue screen.
        </div>
      )}
      {!activeShift && stagedFresh && (
        <div style={{
          background: "rgba(74,164,225,0.1)", border: `1px solid ${BRAND.sky}`,
          borderRadius: "10px", padding: "0.6rem 0.85rem", color: BRAND.sky, fontSize: "0.8rem",
        }}>
          ⚡ Staged for next shift: <b>{stagedSetup.profileName || "ad-hoc settings"}</b>
          {" "}· applied {timeAgo(stagedSetup.appliedAt?.toMillis?.())}
        </div>
      )}

      {/* ── Profile picker ── */}
      <Section
        title="🎪 Event profiles"
        hint="Load a saved setup, tweak anything below, then Apply. Save keeps your tweaks for next time."
      >
        {profiles == null ? (
          <div style={{ color: BRAND.dim, fontSize: "0.8rem", fontStyle: "italic" }}>Loading…</div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
            {profiles.map(p => {
              const active = p.id === selectedId;
              return (
                <button key={p.id} type="button" onClick={() => loadProfile(p)} style={{
                  background: active ? BRAND.gold : BRAND.panel,
                  color: active ? BRAND.dark : BRAND.muted,
                  border: `1px solid ${active ? BRAND.gold : BRAND.border}`,
                  borderRadius: "999px", padding: "0.4rem 0.85rem",
                  fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
                }}>
                  {p.icon || "🎪"} {p.name || "Untitled"}{active && dirty ? " ●" : ""}
                </button>
              );
            })}
            <button type="button" onClick={startNewProfile} style={{
              background: "transparent", color: BRAND.sky,
              border: `1px dashed ${BRAND.sky}`, borderRadius: "999px",
              padding: "0.4rem 0.85rem", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
            }}>
              ＋ New
            </button>
          </div>
        )}
        {profiles != null && profiles.length === 0 && (
          <div style={{ color: BRAND.dim, fontSize: "0.75rem", lineHeight: 1.45 }}>
            No profiles yet — the form below started from this device's last shift settings.
            Adjust it for a venue you work often, then <b>Save as new</b>.
          </div>
        )}
        {selectedId && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <input
              value={draft.name}
              onChange={(e) => setDraft(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Profile name"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button type="button" onClick={deleteProfile} style={{
              background: "transparent", border: `1px solid ${BRAND.border}`, color: BRAND.dim,
              borderRadius: "8px", padding: "0.5rem 0.7rem", fontSize: "0.75rem", cursor: "pointer", whiteSpace: "nowrap",
            }}>
              🗑 Delete
            </button>
          </div>
        )}
      </Section>

      {/* ── Event identity ── */}
      <Section title="📍 Event">
        <input
          value={sd.eventName}
          onChange={(e) => patchShift({ eventName: e.target.value })}
          placeholder="Event name (e.g. Tower Grove Farmers Market)"
          style={inputStyle}
        />
        <AddressAutocomplete
          value={sd.venueAddress}
          onChange={(v) => patchShift({ venueAddress: v })}
          placeholder="Venue address (optional)"
          inputStyle={inputStyle}
        />
        <ToggleRow
          checked={!sd.venueIsPublic}
          onChange={(v) => patchShift({ venueIsPublic: !v })}
          label="Keep this venue private"
          sublabel="Hides the address from keepsake pages and the public site."
          accent={BRAND.gold}
        />
        <div>
          <div style={{ color: BRAND.dim, fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0.35rem" }}>
            Artists this shift
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", marginBottom: "0.4rem" }}>
            {(sd.artistRoster || []).map(name => (
              <div key={name} style={{
                display: "flex", alignItems: "center", gap: "0.25rem",
                background: BRAND.panel, border: `1px solid ${BRAND.border}`,
                borderRadius: "12px", padding: "0.22rem 0.4rem 0.22rem 0.6rem", fontSize: "0.8rem",
              }}>
                <span style={{ color: "rgba(255,255,255,0.75)" }}>{name}</span>
                {(sd.artistRoster || []).length > 1 && (
                  <button type="button" onClick={() => patchShift({ artistRoster: sd.artistRoster.filter(a => a !== name) })}
                    style={{ background: "none", border: "none", color: BRAND.dim, cursor: "pointer", fontSize: "0.9rem", lineHeight: 1, padding: "0 0.1rem" }}>×</button>
                )}
              </div>
            ))}
          </div>
          {newArtist ? (
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <input
                value={artistInput} autoFocus
                onChange={(e) => setArtistInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  const n = artistInput.trim();
                  if (n && !(sd.artistRoster || []).includes(n)) patchShift({ artistRoster: [...(sd.artistRoster || []), n] });
                  setArtistInput(""); setNewArtist(false);
                }}
                placeholder="Artist name…"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button type="button" onClick={() => {
                const n = artistInput.trim();
                if (n && !(sd.artistRoster || []).includes(n)) patchShift({ artistRoster: [...(sd.artistRoster || []), n] });
                setArtistInput(""); setNewArtist(false);
              }} style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.muted, fontSize: "0.82rem", padding: "0.45rem 0.7rem", cursor: "pointer" }}>
                Add
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setNewArtist(true)} style={{
              background: "transparent", border: `1px dashed ${BRAND.border}`, borderRadius: "8px",
              color: BRAND.dim, fontSize: "0.78rem", padding: "0.35rem 0.7rem", cursor: "pointer",
            }}>
              ＋ Add artist
            </button>
          )}
        </div>
      </Section>

      {/* ── Context + ordering ── */}
      <Section title="🎭 Context & ordering">
        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
          {[
            { id: "stream", label: "📺 Stream", hint: "broadcast only" },
            { id: "booth",  label: "🏪 Booth",  hint: "in-person only" },
            { id: "both",   label: "🎪 Both",   hint: "streaming from the booth" },
          ].map(opt => {
            const active = sd.contextMode === opt.id;
            return (
              <button key={opt.id} type="button" onClick={() => patchShift({ contextMode: opt.id })} style={{
                flex: 1, minWidth: "96px",
                background: active ? BRAND.gold : BRAND.panel,
                color: active ? BRAND.dark : BRAND.muted,
                border: `1px solid ${active ? BRAND.gold : BRAND.border}`,
                borderRadius: "8px", padding: "0.5rem 0.5rem", cursor: "pointer",
                display: "flex", flexDirection: "column", alignItems: "center", gap: "2px",
              }}>
                <div style={{ fontSize: "0.82rem", fontWeight: 700 }}>{opt.label}</div>
                <div style={{ fontSize: "0.62rem", opacity: 0.85, fontWeight: 600 }}>{opt.hint}</div>
              </button>
            );
          })}
        </div>
        <div>
          <div style={{ color: "white", fontSize: "0.85rem", fontWeight: 700, marginBottom: "0.4rem" }}>
            📲 QR / Stream ordering (/join)
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {[
              { id: "off",  label: "🚫 Off",    hint: "no QR, /join closed" },
              { id: "free", label: "🎈 Party",  hint: "scans → straight in queue" },
              { id: "paid", label: "🎟 Market", hint: "tier picker + standby" },
            ].map(opt => {
              const active = (sd.joinMode || "off") === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => patchShift({ joinMode: opt.id, livestreamJoinEnabled: opt.id !== "off" })}
                  style={{
                    flex: 1, minWidth: "96px",
                    background: active ? BRAND.gold : BRAND.panel,
                    color: active ? BRAND.dark : BRAND.muted,
                    border: `1px solid ${active ? BRAND.gold : BRAND.border}`,
                    borderRadius: "8px", padding: "0.5rem 0.5rem", cursor: "pointer",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: "2px",
                  }}
                >
                  <div style={{ fontSize: "0.82rem", fontWeight: 700 }}>{opt.label}</div>
                  <div style={{ fontSize: "0.62rem", opacity: 0.85, fontWeight: 600 }}>{opt.hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ── Flash design sets ── */}
      <Section
        title="📚 Flash design sets"
        hint="Curated bundles active on this shift. Leave empty for the standard rotation."
      >
        {flashProfiles == null ? (
          <div style={{ color: BRAND.dim, fontSize: "0.8rem", fontStyle: "italic" }}>Loading…</div>
        ) : flashProfiles.length === 0 ? (
          <div style={{ color: BRAND.dim, fontSize: "0.8rem", fontStyle: "italic" }}>No sets defined yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
            {flashProfiles.map(p => {
              const checked = (sd.activeFlashProfileIds || []).includes(p.id);
              return (
                <label key={p.id} style={{
                  display: "flex", alignItems: "center", gap: "0.55rem",
                  background: checked ? "rgba(252,179,31,0.08)" : BRAND.panel,
                  border: `1px solid ${checked ? BRAND.gold : BRAND.border}`,
                  borderRadius: "8px", padding: "0.42rem 0.6rem", cursor: "pointer", userSelect: "none",
                }}>
                  <input
                    type="checkbox" checked={checked}
                    onChange={(e) => patchShift({
                      activeFlashProfileIds: e.target.checked
                        ? [...(sd.activeFlashProfileIds || []), p.id]
                        : (sd.activeFlashProfileIds || []).filter(id => id !== p.id),
                    })}
                    style={{ accentColor: BRAND.gold, cursor: "pointer" }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "white", fontSize: "0.84rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.name || "Untitled set"}
                    </div>
                    <div style={{ color: BRAND.muted, fontSize: "0.68rem" }}>
                      {(p.designs || []).length} design{(p.designs || []).length === 1 ? "" : "s"}
                      {p.clientName ? ` · ${p.clientName}` : ""}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
        <ToggleRow
          checked={!!sd.includeBaseFlashSet}
          onChange={(v) => patchShift({ includeBaseFlashSet: v })}
          label="Include the standard IP set"
        />
      </Section>

      {/* ── Combo offers ── */}
      <Section
        title="🎁 Combo offers"
        hint="Upsells this event surfaces (Booth spotlight, /join, portal upgrade). Party voice hides all combos automatically."
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          {COMBO_ORDER.map((key) => {
            const combo = COMBOS[key];
            const checked = (sd.activeComboKeys || []).includes(key);
            return (
              <label key={key} style={{
                display: "flex", alignItems: "center", gap: "0.55rem",
                background: checked ? "rgba(252,179,31,0.08)" : BRAND.panel,
                border: `1px solid ${checked ? BRAND.gold : BRAND.border}`,
                borderRadius: "8px", padding: "0.42rem 0.6rem", cursor: "pointer", userSelect: "none",
              }}>
                <input
                  type="checkbox" checked={checked}
                  onChange={(e) => patchShift({
                    activeComboKeys: e.target.checked
                      ? [...(sd.activeComboKeys || []), key]
                      : (sd.activeComboKeys || []).filter(k => k !== key),
                  })}
                  style={{ accentColor: BRAND.gold, cursor: "pointer" }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "white", fontSize: "0.84rem", fontWeight: 600 }}>
                    {combo.emoji} {combo.label} — {fmtCents(sd.pricing?.combos?.[key] ?? combo.priceCents)}
                  </div>
                  <div style={{ color: BRAND.muted, fontSize: "0.68rem" }}>{combo.pitch}</div>
                </div>
              </label>
            );
          })}
        </div>
      </Section>

      {/* ── Road Mode: event pricing ── */}
      <Section
        title="🧳 Event pricing (Road Mode)"
        hint="Per-event prices, add-ons, tax + tipping for travel pop-ups. Lives ONLY on the shift it opens — closing the shift reverts everything. Off = home prices exactly as always."
      >
        <ToggleRow
          checked={!!sd.pricing}
          onChange={(v) => patchShift({ pricing: v ? roadPricingSeed(sd.eventName || draft.name) : null })}
          label="Enable event pricing for this profile"
          sublabel="Checkouts charge these prices as named line items (your Square catalog is never edited), sales tax rides each order, and tipping unlocks at checkout."
          accent={BRAND.gold}
        />
        {sd.pricing && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
            {[
              { label: "⚡ Flash pancake", get: () => sd.pricing.flashCents, set: (c) => patchShift({ pricing: { ...sd.pricing, flashCents: c } }) },
              { label: "📷 Custom photo pancake", get: () => sd.pricing.customCents, set: (c) => patchShift({ pricing: { ...sd.pricing, customCents: c } }) },
              { label: "🎨 Artist Combo", get: () => sd.pricing.combos?.artist, set: (c) => patchShift({ pricing: { ...sd.pricing, combos: { ...sd.pricing.combos, artist: c } } }) },
              { label: "🏆 Collector's Kit", get: () => sd.pricing.combos?.collector, set: (c) => patchShift({ pricing: { ...sd.pricing, combos: { ...sd.pricing.combos, collector: c } } }) },
            ].map((row) => (
              <div key={row.label} style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <div style={{ flex: 1, color: "white", fontSize: "0.84rem", fontWeight: 600 }}>{row.label}</div>
                <PriceInput cents={row.get()} onCents={row.set} />
              </div>
            ))}
            <div style={{ color: BRAND.muted, fontSize: "0.7rem", lineHeight: 1.4 }}>
              Combo upgrade deltas derive automatically (combo − custom). Blank price = that item keeps its home price.
            </div>

            <div style={{ color: BRAND.muted, fontSize: "0.74rem", fontWeight: 700, marginTop: "0.2rem" }}>Add-ons offered at checkout</div>
            {EXTRA_PRESETS.map((preset) => {
              const current = (sd.pricing.extras || []).find((e) => e.key === preset.key) || null;
              return (
                <div key={preset.key} style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                  <input
                    type="checkbox" checked={!!current}
                    onChange={(e) => patchShift({
                      pricing: {
                        ...sd.pricing,
                        extras: e.target.checked
                          ? [...(sd.pricing.extras || []), { ...preset }]
                          : (sd.pricing.extras || []).filter((x) => x.key !== preset.key),
                      },
                    })}
                    style={{ accentColor: BRAND.gold, cursor: "pointer" }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "white", fontSize: "0.82rem", fontWeight: 600 }}>{preset.emoji} {preset.label}</div>
                    <div style={{ color: BRAND.muted, fontSize: "0.66rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{preset.note}</div>
                  </div>
                  {current && (
                    <PriceInput
                      cents={current.cents}
                      onCents={(c) => patchShift({
                        pricing: {
                          ...sd.pricing,
                          extras: (sd.pricing.extras || []).map((x) => x.key === preset.key ? { ...x, cents: c ?? preset.cents } : x),
                        },
                      })}
                      width="72px"
                    />
                  )}
                </div>
              );
            })}

            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginTop: "0.2rem" }}>
              <div style={{ flex: 1, color: "white", fontSize: "0.84rem", fontWeight: 600 }}>Sales tax %</div>
              <input
                inputMode="decimal"
                value={sd.pricing.taxPct ?? ""}
                onChange={(e) => {
                  const n = Number(e.target.value.replace(/[^0-9.]/g, ""));
                  patchShift({ pricing: { ...sd.pricing, taxPct: Number.isFinite(n) && n > 0 ? n : null } });
                }}
                placeholder="e.g. 10.75"
                style={{ ...inputStyle, width: "88px", flex: "0 0 auto", textAlign: "right" }}
              />
            </div>
            <input
              value={sd.pricing.taxLabel || ""}
              onChange={(e) => patchShift({ pricing: { ...sd.pricing, taxLabel: e.target.value } })}
              placeholder="Tax label on receipts (e.g. IL sales tax)"
              style={inputStyle}
            />
            <input
              value={sd.pricing.eventTag || ""}
              onChange={(e) => patchShift({ pricing: { ...sd.pricing, eventTag: e.target.value } })}
              placeholder="Accounting tag on every Square line (e.g. Berwyn Pop-Up)"
              style={inputStyle}
            />
            <input
              value={sd.pricing.squareLocationName || ""}
              onChange={(e) => patchShift({ pricing: { ...sd.pricing, squareLocationName: e.target.value } })}
              placeholder="Square location name for these sales (optional)"
              style={inputStyle}
            />
            <input
              value={sd.pricing.menuNote || ""}
              onChange={(e) => patchShift({ pricing: { ...sd.pricing, menuNote: e.target.value } })}
              placeholder="Menu-card footnote (optional)"
              style={inputStyle}
            />
            <ToggleRow
              checked={sd.pricing.hideStandby === true}
              onChange={(v) => patchShift({ pricing: { ...sd.pricing, hideStandby: v } })}
              label="Hide the free standby tier on /join"
              sublabel="Solo events: every queue slot is paid. Comps still work — issue a ticket from the register and show its QR."
              accent={BRAND.gold}
            />
          </div>
        )}
      </Section>

      {/* ── Overlay theme ── */}
      <Section
        title="🎨 Overlay theme"
        hint="Retints every overlay scene on Apply. Preview any scene with ?theme= in Overlay Previews."
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
          {Object.values(OVERLAY_THEMES).map(t => {
            const active = (draft.overlay.overlayTheme || "classic") === t.id;
            return (
              <button key={t.id} type="button" onClick={() => patchOverlay({ overlayTheme: t.id })} style={{
                background: active ? BRAND.gold : BRAND.panel,
                color: active ? BRAND.dark : BRAND.muted,
                border: `1px solid ${active ? BRAND.gold : BRAND.border}`,
                borderRadius: "8px", padding: "0.5rem 0.8rem",
                fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
                display: "flex", alignItems: "center", gap: "0.45rem",
              }}>
                {/* Swatch strip — the gradient triple is the theme's fingerprint */}
                <span style={{ display: "inline-flex", borderRadius: "4px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.25)" }}>
                  {["grad1", "grad2", "gold"].map(k => (
                    <span key={k} style={{
                      width: "10px", height: "14px",
                      background: t.palette[k] || { grad1: "#1f59db", grad2: "#1d52a0", gold: "#fcb31f" }[k],
                    }} />
                  ))}
                </span>
                {t.label}
              </button>
            );
          })}
        </div>
        <ToggleRow
          checked={!!draft.overlay.boothGalaMode}
          onChange={(v) => patchOverlay({ boothGalaMode: v })}
          label="🥂 Gala mode (Booth display)"
          sublabel="Swaps the Booth bottom pancake tracker for booking-pitch cards (gluten-free poffertjes & catering · get-a-quote QR · live pancake art). For premade / grab-and-go events with no live queue. Stamped on every Apply — applying a profile without it turns it back off."
          accent={BRAND.gold}
        />
        {navigate && (
          <button type="button" onClick={() => navigate("/overlay-previews")} style={{
            alignSelf: "flex-start", background: "none", border: "none",
            color: BRAND.sky, fontSize: "0.76rem", fontWeight: 600, cursor: "pointer",
            padding: 0, textDecoration: "underline",
          }}>
            🎬 Open Overlay Previews →
          </button>
        )}
      </Section>

      {/* ── Overlay copy overrides ── */}
      <Section
        title="📝 Overlay copy"
        hint="Optional per-event overrides. Blank fields keep whatever Stream Setup currently has."
      >
        {OVERLAY_TEXT_FIELDS.map(f => (
          <div key={f.key}>
            <div style={{ color: BRAND.muted, fontSize: "0.72rem", fontWeight: 600, marginBottom: "0.25rem" }}>{f.label}</div>
            <input
              value={draft.overlay[f.key] || ""}
              onChange={(e) => patchOverlay({ [f.key]: e.target.value })}
              placeholder={streamConfig?.[f.key] ? `Current: ${streamConfig[f.key]}` : "Leave blank to keep current"}
              style={inputStyle}
            />
          </div>
        ))}
      </Section>

      {/* ── Messaging suppressions ── */}
      <ShiftMessagingCard
        marqueeMessages={streamConfig?.marqueeMessages || []}
        showcaseItems={showcaseConfig?.items || []}
        contextMode={sd.contextMode}
        disabledMarqueeTexts={sd.disabledMarqueeTexts || []}
        disabledShowcaseIds={sd.disabledShowcaseIds || []}
        onToggleMarquee={(text) => patchShift({
          disabledMarqueeTexts: (sd.disabledMarqueeTexts || []).includes(text)
            ? (sd.disabledMarqueeTexts || []).filter(t => t !== text)
            : [...(sd.disabledMarqueeTexts || []), text],
        })}
        onToggleShowcase={(id) => patchShift({
          disabledShowcaseIds: (sd.disabledShowcaseIds || []).includes(id)
            ? (sd.disabledShowcaseIds || []).filter(x => x !== id)
            : [...(sd.disabledShowcaseIds || []), id],
        })}
        intro="Saved with the profile — applied to the shift each time you use it."
      />

      {/* ── Sticky action bar ── */}
      <div style={{
        position: "sticky", bottom: 0, marginTop: "0.3rem",
        background: BRAND.bg, borderTop: `1px solid ${BRAND.divider}`,
        padding: "0.75rem 0 0.5rem",
        display: "flex", gap: "0.5rem", flexWrap: "wrap",
      }}>
        <button type="button" onClick={saveProfile} disabled={saving} style={{
          flex: 1, minWidth: "130px",
          background: BRAND.panel, color: dirty ? "white" : BRAND.muted,
          border: `1px solid ${dirty ? BRAND.gold : BRAND.border}`,
          borderRadius: "10px", padding: "0.7rem 0.8rem",
          fontSize: "0.88rem", fontWeight: 700, cursor: "pointer",
        }}>
          {saving ? "Saving…" : selectedId ? `💾 Save${dirty ? " ●" : ""}` : "💾 Save as profile"}
        </button>
        {selectedId && (
          <button type="button" onClick={saveAsNew} disabled={saving} style={{
            background: BRAND.panel, color: BRAND.muted, border: `1px solid ${BRAND.border}`,
            borderRadius: "10px", padding: "0.7rem 0.8rem",
            fontSize: "0.88rem", fontWeight: 700, cursor: "pointer",
          }}>
            Save as new…
          </button>
        )}
        <button type="button" onClick={applyDraft} disabled={applying} style={{
          flex: 2, minWidth: "170px",
          background: BRAND.green, color: "white", border: "none",
          borderRadius: "10px", padding: "0.7rem 0.8rem",
          fontSize: "0.92rem", fontWeight: 800, cursor: "pointer",
          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        }}>
          {applying ? "Applying…" : activeShift ? "⚡ Apply (overlays now · next shift staged)" : "⚡ Apply for next shift"}
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", left: "50%", bottom: "5.5rem", transform: "translateX(-50%)",
          background: BRAND.raised, color: "white", border: `1px solid ${BRAND.border}`,
          padding: "0.65rem 1.1rem", borderRadius: "999px",
          fontSize: "0.88rem", fontWeight: 700,
          boxShadow: "0 6px 24px rgba(0,0,0,0.45)", zIndex: 9999, pointerEvents: "none",
          whiteSpace: "nowrap",
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
